import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Drawer from "@mui/material/Drawer";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { WidgetContext } from "src/contexts/WidgetContext";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isIOS, isAndroid, isWebNotCapacitor } from "src/utils/platformUtils";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { fetchPublishedArticleById, fetchPublishedArticleBySlug, fetchPublishedArticles, PublishedArticleDetail, PublishedArticleSummary } from "src/api/articles";
import ArticlesResponse, { formatArticleTimestamp } from "./ArticlesResponse";
import ArticlesSign from "./ArticlesSign";
import { trackFeatureTimeSpent } from "src/utils/analyticsCapFirebase";
import articleMarkdownStyles from "./articlesDrawer.module.css";
import { buildArticleCanonicalPath } from "src/config/seo.config";
import styles from "../../content/pages/homepage.module.css";
type ViewMode = "list" | "detail";

const MONGO_OBJECT_ID_RE = /^[a-f\d]{24}$/i;

function isValidArticleMongoId(id: string): boolean {
  return MONGO_OBJECT_ID_RE.test(id.trim());
}

function isValidArticleSlug(slug: string): boolean {
  const trimmed = slug.trim();
  return trimmed.length > 0 && trimmed.length <= 200 && !isValidArticleMongoId(trimmed);
}

/** Desktop SEO/GEO: update the address bar without React Router navigation (keeps HomePage mounted). */
function pushDesktopArticlesUrl(path: string) {
  if (typeof window === "undefined") return;
  if (`${window.location.pathname}${window.location.search}` === path) return;
  window.history.pushState({ articlesDesktopSeo: true }, "", path);
}

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const normalizeSearch = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function stripMarkdown(text: string): string {
  return text
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/(```[\s\S]*?```|`[^`]*`)/g, " ")
    .replace(/^>\s?/gm, "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/(\*\*|__|\*|_|~~)/g, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

function highlightSearch(text: string, search: string): React.ReactNode {
  if (!search.trim()) return text;
  const lower = search.toLowerCase().trim();
  const parts = text.split(new RegExp(`(${escapeRegex(search)})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === lower ? (
      <Box key={i} component="span" sx={{ bgcolor: "#ff672d", borderRadius: 0, color: "#000" }}>
        {part}
      </Box>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function buildBodyMatchPreview(body: string, search: string): string | null {
  if (!search.trim()) return null;
  const normalizedBody = stripMarkdown(body);
  if (!normalizedBody) return null;
  const lowerBody = normalizedBody.toLowerCase();
  const lowerSearch = search.toLowerCase().trim();
  const matchIndex = lowerBody.indexOf(lowerSearch);
  if (matchIndex === -1) return null;

  const start = Math.max(0, matchIndex - 45);
  const end = Math.min(normalizedBody.length, matchIndex + search.length + 65);
  const prefix = start > 0 ? "..." : "";
  const suffix = end < normalizedBody.length ? "..." : "";
  return `${prefix}${normalizedBody.slice(start, end).trim()}${suffix}`;
}

interface ArticlesPartnerListProps {
  onClose?: () => void;
  standalone?: boolean;
}

function ArticlesPartnerList({ onClose, standalone = false }: ArticlesPartnerListProps) {
  const navigate = useNavigate();
  const { slug: slugParam } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  const { isDrawerOpen, setIsDrawerOpen, selectedTeamInfo, NON_LOGGED_IN_CONFIG, aiSettings } = useContext(WidgetContext);
  const isOpen = standalone ? true : isDrawerOpen;

  const [drawerOpenTime, setDrawerOpenTime] = useState<number | null>(null);
  const [view, setView] = useState<ViewMode>("list");
  const [searchText, setSearchText] = useState("");
  const [articles, setArticles] = useState<PublishedArticleSummary[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PublishedArticleDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Desktop drawer SEO: remember /home path so we can restore it without remounting HomePage.
  const desktopReturnPathRef = useRef<string | null>(null);

  // Standalone /articles/:slug deep link — skip list fetch (detail only).
  const standaloneArticleSlug = useMemo(() => {
    if (!standalone) return null;
    const slug = slugParam?.trim();
    if (!slug) return null;
    if (isValidArticleSlug(slug) || isValidArticleMongoId(slug)) return slug;
    return null;
  }, [standalone, slugParam]);

  useEffect(() => {
    if (isOpen && !drawerOpenTime) {
      setDrawerOpenTime(Date.now());
    }
  }, [isOpen, drawerOpenTime]);

  // Desktop web only: sync address bar to /articles while keeping the in-home drawer (no HomePage remount).
  useEffect(() => {
    if (standalone || !isWebNotCapacitor()) return;
    if (!isOpen) return;
    if (!desktopReturnPathRef.current) {
      desktopReturnPathRef.current = `${window.location.pathname}${window.location.search}`;
    }
    if (!window.location.pathname.startsWith("/articles")) {
      pushDesktopArticlesUrl("/articles");
    }
  }, [isOpen, standalone]);

  // Desktop web only: browser back/forward while drawer is open.
  useEffect(() => {
    if (standalone || !isWebNotCapacitor()) return;

    const onPopState = () => {
      const path = window.location.pathname;
      if (path.startsWith("/articles")) {
        const slugFromPath = path.replace(/^\/articles\/?/, "").split("/")[0]?.trim();
        const idParam = new URLSearchParams(window.location.search).get("id")?.trim();
        const deepLinkKey =
          (slugFromPath && (isValidArticleSlug(slugFromPath) || isValidArticleMongoId(slugFromPath)) && slugFromPath) ||
          (idParam && (isValidArticleSlug(idParam) || isValidArticleMongoId(idParam)) ? idParam : null);
        if (deepLinkKey) {
          setSelectedId(deepLinkKey);
          setView("detail");
          setDetail(null);
          setDetailError(null);
        } else {
          setView("list");
          setSelectedId(null);
          setDetail(null);
          setDetailError(null);
        }
        return;
      }
      // Left /articles URL → close drawer and stay on HomePage (already mounted).
      desktopReturnPathRef.current = null;
      setView("list");
      setSelectedId(null);
      setDetail(null);
      setDetailError(null);
      setSearchText("");
      onClose?.();
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [standalone, onClose]);

  const loadList = useCallback(async () => {
    if (!isOpen || isAuthLoading) return;
    // Deep-linked standalone article page only needs detail fetch.
    if (standaloneArticleSlug) return;
    setListLoading(true);
    setListError(null);
    try {
      let token: string | undefined;
      if (isAuthenticated) {
        token = await getSafeAccessAuth0Token();
      }
      const rows = await fetchPublishedArticles(token);
      setArticles(rows);
    } catch {
      setListError("Could not load articles.");
    } finally {
      setListLoading(false);
    }
  }, [getSafeAccessAuth0Token, isAuthenticated, isAuthLoading, isOpen, standaloneArticleSlug]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const loadDetail = useCallback(async () => {
    if (!selectedId || !isOpen || isAuthLoading) return;
    const detailKey = detail?.slug?.trim() || detail?.id;
    if (detail && detailKey === selectedId) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      let token: string | undefined;
      if (isAuthenticated) {
        token = await getSafeAccessAuth0Token();
      }
      // selectedId may be a slug (preferred GEO URL) or a legacy mongo ObjectId.
      const doc = isValidArticleMongoId(selectedId)
        ? await fetchPublishedArticleById(selectedId, token)
        : await fetchPublishedArticleBySlug(selectedId, token);
      setDetail(doc);
    } catch {
      setDetailError("Could not load this article.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [getSafeAccessAuth0Token, isAuthenticated, isAuthLoading, isOpen, selectedId, detail]);

  useEffect(() => {
    if (view !== "detail" || !selectedId) return;
    void loadDetail();
  }, [loadDetail, selectedId, view]);

  // Deep link + URL sync: /articles/:slug (preferred) or legacy /articles?id=<slug>.
  // Prefer slug value; also accept legacy mongo ObjectId.
  useEffect(() => {
    if (!standalone || isAuthLoading) return;

    // Legacy ?id= → clean URL redirect (keeps old email/share links working).
    const idParam = searchParams.get("id")?.trim();
    if (idParam && (isValidArticleSlug(idParam) || isValidArticleMongoId(idParam))) {
      navigate(buildArticleCanonicalPath(idParam), { replace: true });
      return;
    }

    const slugFromPath = slugParam?.trim();
    const deepLinkKey =
      slugFromPath && (isValidArticleSlug(slugFromPath) || isValidArticleMongoId(slugFromPath))
        ? slugFromPath
        : null;
    if (!deepLinkKey) {
      if (view === "detail") {
        setView("list");
        setSelectedId(null);
        setDetail(null);
        setDetailError(null);
      }
      return;
    }
    if (selectedId === deepLinkKey && view === "detail") return;
    if (selectedId !== deepLinkKey) {
      setDetail(null);
      setDetailError(null);
    }
    setSelectedId(deepLinkKey);
    setView("detail");
  }, [standalone, isAuthLoading, searchParams, slugParam, selectedId, view, navigate]);

  const handleClose = () => {
    if (standalone) return;

    if (drawerOpenTime) {
      const timeSpentMs = Date.now() - drawerOpenTime;
      const timeSpentSeconds = Math.round(timeSpentMs / 1000);
      const currentPlatform = selectedTeamInfo?.platformId || "unknown";
      const authId = user?.sub ?? NON_LOGGED_IN_CONFIG?.NON_LOGGED_IN_AUTH_ID ?? "unknown";
      if (timeSpentSeconds > 0) {
        trackFeatureTimeSpent(authId, "articles", timeSpentSeconds, currentPlatform);
      }
      setDrawerOpenTime(null);
    }

    setView("list");
    setSelectedId(null);
    setDetail(null);
    setSearchText("");
    // Desktop SEO: restore /home URL without remounting HomePage.
    if (isWebNotCapacitor() && desktopReturnPathRef.current) {
      const returnPath = desktopReturnPathRef.current;
      desktopReturnPathRef.current = null;
      window.history.pushState(null, "", returnPath);
    }
    // Sync close: onClose clears thoughts + closes drawer (avoids sign flash before clear)
    onClose?.();
  };

  const goBackToList = () => {
    setView("list");
    setSelectedId(null);
    setDetail(null);
    setDetailError(null);
    if (standalone) {
      navigate("/articles", { replace: true });
    } else if (!standalone && isWebNotCapacitor()) {
      pushDesktopArticlesUrl("/articles");
    }
  };

  const handleHeaderBack = () => {
    if (view === "detail") {
      goBackToList();
      return;
    }
    if (standalone) {
      navigate("/");
    }
  };

  const openArticle = (article: PublishedArticleSummary) => {
    const urlValue = article.slug?.trim() || (isValidArticleMongoId(article.id) ? article.id.trim() : "");
    if (standalone) {
      if (urlValue) {
        navigate(buildArticleCanonicalPath(urlValue));
        return;
      }
    }
    const detailKey = article.slug?.trim() || article.id;
    setSelectedId(detailKey);
    setView("detail");
    setDetail(null);
    // Desktop drawer: sync SEO URL without leaving HomePage.
    if (!standalone && isWebNotCapacitor()) {
      if (urlValue) {
        pushDesktopArticlesUrl(buildArticleCanonicalPath(urlValue));
      }
    }
  };

  const headerTitle = useMemo(() => {
    if (view === "list") return "Articles";
    if (detail?.headline) return "Articles";
    return "Articles";
  }, [detail?.headline, view]);

  const filteredArticles = useMemo(() => {
    if (!searchText.trim()) return articles;
    const query = normalizeSearch(searchText);
    return articles.filter((article) => {
      const headline = normalizeSearch(article.headline);
      const author = normalizeSearch(article.author);
      const authorLabel = normalizeSearch(`by ${article.author}`);
      const body = normalizeSearch(article.body);
      return headline.includes(query) || author.includes(query) || authorLabel.includes(query) || body.includes(query);
    });
  }, [articles, searchText]);

  const content = (
    <Fade in={isOpen} timeout={700}>
      <Box
        className={styles.noScrollbar}
        sx={{
          bgcolor: "#000000",
          // Detail: fill parent so sign footer stays pinned; list/standalone keep previous scroll behavior
          height: view === "detail" || !standalone ? "100%" : "auto",
          overflow: view === "detail" || !standalone ? "hidden" : "visible",
          position: "relative",
          touchAction: standalone ? "auto" : "none",
          "& > *": {
            touchAction: "pan-y",
          },
          paddingBottom: isAndroid ? "max(env(safe-area-inset-bottom), 15px)" : "0px",
          pointerEvents: "auto",
          display: "flex",
          flexDirection: "column",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1,
            py: 1,
            borderBottom: `1px solid ${PureLightThemeCaaS.colors.alpha.white[20]}`,
          }}
        >
          <Box sx={{ width: 40, display: "flex", justifyContent: "flex-start" }}>
            {view === "detail" || standalone ? (
              <IconButton aria-label="back" onClick={handleHeaderBack} size="small" sx={{ color: PureLightThemeCaaS.colors.alpha.white[100] }}>
                <ArrowBackIcon />
              </IconButton>
            ) : null}
          </Box>
          <Typography
            sx={{
              flex: 1,
              textAlign: "center",
              fontSize: 16,
              fontWeight: 700,
              color: PureLightThemeCaaS.colors.alpha.white[100],
            }}
          >
            {headerTitle}
          </Typography>
          <Box sx={{ width: 40, display: "flex", justifyContent: "flex-end" }}>
            {!standalone ? (
              <IconButton aria-label="close" onClick={handleClose} size="small" sx={{ color: PureLightThemeCaaS.colors.alpha.white[100] }}>
                <CloseIcon />
              </IconButton>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none", px: 2, pb: 2, pt: 1.5 }}>
          {view === "list" && (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", pt: 0.5, pb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search articles"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  autoComplete="off"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon sx={{ color: PureLightThemeCaaS.colors.alpha.white[70], fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    maxWidth: 360,
                    "& .MuiOutlinedInput-root": {
                      height: "38px",
                      borderRadius: 999,
                      bgcolor: "#111111",
                      color: PureLightThemeCaaS.colors.alpha.white[70],
                      "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.28)" },
                      "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                    },
                    "& .MuiOutlinedInput-input": {
                      color: PureLightThemeCaaS.colors.alpha.white[100],
                      WebkitTextFillColor: PureLightThemeCaaS.colors.alpha.white[100],
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: PureLightThemeCaaS.colors.alpha.white[50],
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              {listError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {listError}
                </Alert>
              )}

              {listLoading ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <CircularProgress size={40} sx={{ color: PureLightThemeCaaS.colors.alpha.orange[100] }} />
                </Box>
              ) : filteredArticles.length === 0 ? (
                <Typography sx={{ color: PureLightThemeCaaS.colors.alpha.white[100], textAlign: "center", mt: 2 }}>
                  No articles match with your search
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {filteredArticles.map((a) => (
                    <ArticleListCard key={a.id} article={a} searchText={searchText} onOpen={() => openArticle(a)} />
                  ))}
                </Stack>
              )}
            </>
          )}

          {view === "detail" && <ArticlesResponse loading={detailLoading} error={detailError} article={detail} />}
        </Box>

        {/* Outside scroll = always visible (share SignUpBanner pattern). Sticky cannot do this. */}
        {view === "detail" && (
          <Box sx={{ flexShrink: 0, position: "relative", bgcolor: "#000000", pb: "5px",mb:"10px" }}>
            <Box
              className={articleMarkdownStyles.ArticlesFadeEffect}
              sx={{ position: "absolute", left: 0, right: 0, bottom: "100%", pointerEvents: "none" }}
            />
            <ArticlesSign />
          </Box>
        )}
      </Box>
    </Fade>
  );

  return (
    standalone ? (
      <Box
        sx={{
          // Keep app column width on resize: 720px max, shrink only below that (not at lg breakpoint).
          width: "100%",
          maxWidth: "720px",
          margin: "0 auto",
          // Web standalone: fill ArticlesPage (100vh). No 56px app header on this route.
          height: isIOS || isAndroid
            ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
            : standalone && isWebNotCapacitor()
              ? "100%"
              : (!aiSettings.quan ? "calc(100vh - 56px)" : "100vh"),
          border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          overflowY: "auto",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {content}
      </Box>
    ) : (
      <Drawer
        anchor="bottom"
        open={isDrawerOpen}
        hideBackdrop={true}
        sx={{
          pointerEvents: "none",
        }}
        ModalProps={{
          keepMounted: true,
          sx: {
            pointerEvents: "auto",
            top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
            zIndex: 1450,
          },
        }}
        PaperProps={{
          sx: {
            pointerEvents: "auto",
            width: {
              xs: "100%",
              sm: "720px",
              md: "720px",
              lg: "720px",
            },
            margin: {
              xs: "0",
              sm: "0 auto",
              md: "0 auto",
              lg: "0 auto",
            },
            height: isIOS || isAndroid
              ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
              : (!aiSettings.quan ? "calc(100vh - 56px)" : "100%"),
            border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            overflowY: "auto",
          },
        }}
      >
        {content}
      </Drawer>
    )
  );
}

function ArticleListCard({
  article,
  searchText,
  onOpen,
}: {
  article: PublishedArticleSummary;
  searchText: string;
  onOpen: () => void;
}) {
  const ts = formatArticleTimestamp(article.date);
  const bodyPreview = buildBodyMatchPreview(article.body, searchText);
  // Real href for crawlers; preventDefault keeps existing SPA/drawer open behavior.
  const articleHref = buildArticleCanonicalPath((article.slug?.trim() || article.id).trim());
  return (
    <Paper
      component="a"
      href={articleHref}
      elevation={0}
      onClick={(e) => {
        e.preventDefault();
        onOpen();
      }}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        gap: 1.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: "#1f2229",
        border: "1px solid rgba(255,255,255,0.08)",
        cursor: "pointer",
        textDecoration: "none",
        color: "inherit",
        "&:hover": { borderColor: "rgba(255,103,45,0.45)" },
      }}
    >
      <Box
        component="img"
        src={article.imageUrl}
        alt=""
        sx={{
          width: 72,
          height: 72,
          borderRadius: 1.5,
          objectFit: "cover",
          flexShrink: 0,
          bgcolor: "#111",
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.5 }}>
        <Typography
          sx={{
            color: "#fff",
            fontWeight: 800,
            fontSize: 13,
            lineHeight: 1.25,
            textTransform: "uppercase",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {highlightSearch(article.headline, searchText)}
        </Typography>
        <Typography sx={{ fontSize: 11, lineHeight: 1.35 }}>
          <Box component="span" sx={{ color: PureLightThemeCaaS.colors.alpha.orange[100], fontWeight: 800 }}>
            BY {highlightSearch(article.author.toUpperCase(), searchText)}
          </Box>
          <Box component="span" sx={{ color: PureLightThemeCaaS.colors.alpha.white[50], fontWeight: 600 }}>
            {" "}
            • {ts}
          </Box>
        </Typography>
        {bodyPreview && (
          <Typography
            sx={{
              color: PureLightThemeCaaS.colors.alpha.white[70],
              fontSize: 11,
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {highlightSearch(bodyPreview, searchText)}
          </Typography>
        )}
        {article.geoText?.trim() ? (
          <span className={articleMarkdownStyles.ArticleGeoHidden}>{article.geoText.trim()}</span>
        ) : null}
      </Box>
    </Paper>
  );
}

export default ArticlesPartnerList;